// ===============================
// HotelManagerLogin.js
// Firebase Manager Login + Hotel Selection + 4 Digit PIN
// ===============================

import React, { useEffect, useRef, useState } from "react";

import { app, auth } from "../firebase";

import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";

import {
  getFunctions,
  httpsCallable,
} from "firebase/functions";

import { useNavigate } from "react-router-dom";

// Shared Manager Firebase Auth account UID
const MANAGER_UID =
  "qrvQ8zvbtobSxHPkVGgx2c63t9B2";

const ADMIN_UID =
  "imXv7XiyoYSxJRg9kLzo7jhrbJI3";

const functions = getFunctions(app, "asia-south1");
const getManagerHotelsCallable = httpsCallable(functions, "getManagerHotels");
const verifyManagerHotelPinCallable = httpsCallable(functions, "verifyManagerHotelPin");

const getSavedManagerHotelId = () => localStorage.getItem("managerHotelId") || sessionStorage.getItem("managerHotelId") || "";
const hasSavedManagerSession = () => localStorage.getItem("managerLoggedIn") === "true" || sessionStorage.getItem("managerLoggedIn") === "true";

function HotelManagerLogin() {
  const [hotels, setHotels] = useState([]);

  const [selectedHotelId, setSelectedHotelId] =
    useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");

  const [loadingHotels, setLoadingHotels] =
    useState(true);

  const [loggingIn, setLoggingIn] =
    useState(false);

  const navigate = useNavigate();
  const manualLoginRef = useRef(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      // Manual login ke beech Firebase auth event turant fire hota hai.
      // PIN/session save hone se pehle redirect karne par login page/dashboard
      // flicker karta tha. Manual flow me redirect sirf handleLogin karega.
      if (manualLoginRef.current) return;

      if (
        (u?.uid === MANAGER_UID || u?.uid === ADMIN_UID) &&
        getSavedManagerHotelId() &&
        hasSavedManagerSession()
      ) {
        navigate("/manager-dashboard", { replace: true });
      }
    });

    return () => unsub();
  }, [navigate]);

  // ===============================
  // LOAD HOTEL LIST
  // ===============================

  useEffect(() => {
    const fetchHotels = async () => {
      setLoadingHotels(true);

      try {
        const response = await getManagerHotelsCallable({});
        const hotelList = Array.isArray(response?.data?.hotels)
          ? response.data.hotels
          : [];

        setHotels(hotelList);
      } catch (error) {
        console.error(
          "Secure hotel list load error:",
          error
        );

        setHotels([]);
        alert(
          "Hotel list load nahi hui: " +
            (error?.message || error)
        );
      } finally {
        setLoadingHotels(false);
      }
    };

    fetchHotels();
  }, []);

  // ===============================
  // MANAGER LOGIN
  // ===============================

  const handleLogin = async (event) => {
    event.preventDefault();

    if (loggingIn) return;

    const cleanEmail = email
      .trim()
      .toLowerCase();

    const cleanPin = String(pin || "")
      .trim();

    if (!cleanEmail) {
      return alert(
        "Manager email enter karo."
      );
    }

    if (!password) {
      return alert(
        "Manager password enter karo."
      );
    }

    if (!selectedHotelId) {
      return alert(
        "Please select hotel."
      );
    }

    if (!cleanPin) {
      return alert(
        "Please enter 4 digit PIN."
      );
    }

    if (!/^\d{4}$/.test(cleanPin)) {
      return alert(
        "PIN exactly 4 digits ka hona chahiye."
      );
    }

    const selectedHotel = hotels.find(
      (hotel) =>
        hotel.id === selectedHotelId
    );

    if (!selectedHotel) {
      return alert(
        "Selected hotel nahi mila."
      );
    }

    setLoggingIn(true);
    manualLoginRef.current = true;

    // Purani manager session keys manual login ko hijack na karein.
    [sessionStorage, localStorage].forEach((storage) => {
      storage.removeItem("managerHotelId");
      storage.removeItem("managerHotelName");
      storage.removeItem("managerLoggedIn");
    });

    try {
      // Firebase Manager Account Login
      const userCredential =
        await signInWithEmailAndPassword(
          auth,
          cleanEmail,
          password
        );

      const loggedInUser =
        userCredential.user;

      // Only manager UID allowed
      if (
        loggedInUser.uid !== MANAGER_UID &&
        loggedInUser.uid !== ADMIN_UID
      ) {
        await signOut(auth);

        return alert(
          "Ye account authorised Admin/Manager account nahi hai."
        );
      }

      // PHASE 3B: hotel PIN backend par verify hota hai.
      // Saved PIN browser ko kabhi return nahi hota.
      const pinResponse =
        await verifyManagerHotelPinCallable({
          hotelId: selectedHotel.id,
          pin: cleanPin,
        });

      const pinResult = pinResponse?.data || {};

      if (!pinResult.ok) {
        throw new Error("Wrong Hotel PIN!");
      }

      const managerHotelName =
        pinResult.hotelName ||
        selectedHotel.hotelName ||
        selectedHotel.name ||
        selectedHotel.id;

      // Persistent manager session: current tab + installed PWA/browser reopen.
      [sessionStorage, localStorage].forEach((storage) => {
        storage.setItem("managerHotelId", selectedHotel.id);
        storage.setItem("managerHotelName", managerHotelName);
        storage.setItem("managerLoggedIn", "true");
      });

      navigate(
        "/manager-dashboard",
        {
          replace: true,
        }
      );
    } catch (error) {
      console.error(
        "Manager login error:",
        error
      );

      let message =
        error?.message ||
        "Manager login failed.";

      if (
        String(error?.code || "").includes("permission-denied") ||
        String(error?.message || "").includes("Wrong Hotel PIN")
      ) {
        message = "Wrong Hotel PIN!";
      }

      if (
        error?.code ===
        "auth/invalid-credential"
      ) {
        message =
          "Manager email ya password galat hai.";
      } else if (
        error?.code ===
        "auth/user-not-found"
      ) {
        message =
          "Manager account nahi mila.";
      } else if (
        error?.code ===
        "auth/wrong-password"
      ) {
        message =
          "Manager password galat hai.";
      } else if (
        error?.code ===
        "auth/too-many-requests"
      ) {
        message =
          "Bahut zyada login attempts hue hain. Thodi der baad try karo.";
      } else if (
        error?.code ===
        "auth/network-request-failed"
      ) {
        message =
          "Internet connection check karo.";
      }

      // Firebase email/password login ho gaya lekin PIN/backend step fail hua
      // to half-authenticated manager session nahi chhodenge.
      if (auth.currentUser) {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.warn("Login cleanup sign-out failed:", signOutError);
        }
      }

      alert(message);
    } finally {
      manualLoginRef.current = false;
      setLoggingIn(false);
    }
  };

  // ===============================
  // UI
  // ===============================

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f5f7fb",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 430,
          background: "#fff",
          borderRadius: 20,
          padding: 24,
          boxShadow:
            "0 12px 30px rgba(0,0,0,0.12)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: 20,
          }}
        >
          <img
            src="/images/el-logo.png"
            alt="Hotel Dev Palace"
            style={{
              width: 90,
              maxWidth: "100%",
            }}
          />

          <h2
            style={{
              marginTop: 10,
              marginBottom: 5,
            }}
          >
            Hotel Manager Login
          </h2>

          <p
            style={{
              margin: 0,
              color: "#64748b",
              fontSize: 14,
            }}
          >
            Manager account aur hotel PIN
            se login karein
          </p>
        </div>

        <form onSubmit={handleLogin}>
          {/* Manager Email */}

          <label
            style={{
              fontWeight: 700,
              display: "block",
            }}
          >
            Manager Email
          </label>

          <input
            type="email"
            value={email}
            onChange={(event) =>
              setEmail(event.target.value)
            }
            placeholder="Enter manager email"
            autoComplete="email"
            required
            style={{
              width: "100%",
              padding: 12,
              marginTop: 8,
              marginBottom: 16,
              borderRadius: 12,
              border:
                "1px solid #cbd5e1",
              boxSizing: "border-box",
              fontSize: 15,
            }}
          />

          {/* Manager Password */}

          <label
            style={{
              fontWeight: 700,
              display: "block",
            }}
          >
            Manager Password
          </label>

          <input
            type="password"
            value={password}
            onChange={(event) =>
              setPassword(event.target.value)
            }
            placeholder="Enter manager password"
            autoComplete="current-password"
            required
            style={{
              width: "100%",
              padding: 12,
              marginTop: 8,
              marginBottom: 16,
              borderRadius: 12,
              border:
                "1px solid #cbd5e1",
              boxSizing: "border-box",
              fontSize: 15,
            }}
          />

          {/* Select Hotel */}

          <label
            style={{
              fontWeight: 700,
              display: "block",
            }}
          >
            Select Hotel
          </label>

          <select
            value={selectedHotelId}
            onChange={(event) => {
              setSelectedHotelId(
                event.target.value
              );

              setPin("");
            }}
            disabled={loadingHotels}
            style={{
              width: "100%",
              padding: 12,
              marginTop: 8,
              marginBottom: 16,
              borderRadius: 12,
              border:
                "1px solid #cbd5e1",
              boxSizing: "border-box",
              background: "#fff",
              fontSize: 15,
            }}
          >
            <option value="">
              {loadingHotels
                ? "Loading hotels..."
                : "-- Select Hotel --"}
            </option>

            {!loadingHotels &&
              hotels.map((hotel) => (
                <option
                  key={hotel.id}
                  value={hotel.id}
                >
                  {hotel.hotelName ||
                    hotel.name ||
                    hotel.id}
                </option>
              ))}
          </select>

          {/* Hotel PIN */}

          <label
            style={{
              fontWeight: 700,
              display: "block",
            }}
          >
            4 Digit Hotel PIN
          </label>

          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={(event) => {
              const onlyNumbers =
                event.target.value.replace(
                  /\D/g,
                  ""
                );

              setPin(
                onlyNumbers.slice(0, 4)
              );
            }}
            placeholder="Enter 4 digit PIN"
            required
            style={{
              width: "100%",
              padding: 12,
              marginTop: 8,
              marginBottom: 18,
              borderRadius: 12,
              border:
                "1px solid #cbd5e1",
              boxSizing: "border-box",
              fontSize: 16,
              letterSpacing: 4,
            }}
          />

          {/* Login Button */}

          <button
            type="submit"
            disabled={
              loggingIn ||
              loadingHotels
            }
            style={{
              width: "100%",
              padding: 13,
              border: "none",
              borderRadius: 12,
              background:
                loggingIn ||
                loadingHotels
                  ? "#94a3b8"
                  : "#1d4ed8",
              color: "#fff",
              fontWeight: 800,
              cursor:
                loggingIn ||
                loadingHotels
                  ? "not-allowed"
                  : "pointer",
              fontSize: 16,
            }}
          >
            {loggingIn
              ? "Logging In..."
              : "Login"}
          </button>
        </form>

        <div
          style={{
            marginTop: 14,
            fontSize: 13,
            color: "#64748b",
            textAlign: "center",
          }}
        >
          {loadingHotels
            ? "Loading hotels..."
            : hotels.length === 0
            ? "No active hotels found."
            : `${hotels.length} hotel(s) available`}
        </div>
      </div>
    </div>
  );
}

export default HotelManagerLogin;